import { useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { useMarkRequestDone, useRequests } from '@/hooks/useRequests';
import { formatAddedDate } from '@/lib/formatters';

// The URL is user-supplied; only render it as a clickable link when it's an
// http(s) URL. A `javascript:`/`data:` URL would execute in-origin on click
// (refresh tokens live in localStorage), so anything else is shown as text.
function safeHttpUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    return /^https?:$/.test(new URL(url).protocol) ? url : null;
  } catch {
    return null;
  }
}

// Staff-only queue of user-submitted "add this coffee/roaster" requests (RFC
// 016). Rendering is gated by the is_admin claim in App.tsx; the API's admin
// middleware is the real gate. Fulfilment stays manual — an admin onboards the
// roaster through crawl setup, then marks the request done so it drops off.
export function RequestsPage() {
  const navigate = useNavigate();
  const { requests, isLoading, error, refetch } = useRequests();
  const { markDone, isSubmitting } = useMarkRequestDone();

  async function handleDone(id: string) {
    await markDone(id);
    await refetch();
  }

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="requests-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/me')}
            aria-label="Back to account"
            data-testid="requests-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Admin
          </span>
        </div>

        <h1 className="mb-1 mt-1 text-2xl font-bold text-black">Requests</h1>
        <p className="mb-5 text-sm text-black/50">
          Coffees and roasters people asked us to add. Onboard the roaster through crawl setup, then
          mark the request done.
        </p>

        {isLoading ? (
          <LoadingSpinner label="Loading requests" testId="requests-loading" />
        ) : error ? (
          <p className="text-sm text-black/50" data-testid="requests-error">
            Couldn&apos;t load requests. Refresh to try again.
          </p>
        ) : requests.length === 0 ? (
          <div
            className="rounded-xl bg-wolisho-card px-4 py-10 text-center text-sm text-black/50"
            data-testid="requests-empty"
          >
            No pending requests.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl bg-wolisho-card py-1">
            {requests.map((request, index) => (
              <div key={request.id}>
                {index > 0 && <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />}
                <div className="flex items-start gap-3 px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    {safeHttpUrl(request.url) ? (
                      <a
                        href={safeHttpUrl(request.url) ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate text-sm font-bold text-wolisho-red hover:underline"
                      >
                        {request.url}
                      </a>
                    ) : (
                      <p className="truncate text-sm font-bold text-black">
                        {request.url ?? request.name}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-black/40" data-ph-mask>
                      {request.requester?.name ?? 'Unknown'} · {formatAddedDate(request.createdAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleDone(request.id)}
                    disabled={isSubmitting}
                    data-testid={`request-done-${request.id}`}
                    className="shrink-0 rounded-lg border border-wolisho-brown/20 px-3 py-1.5 text-xs font-bold text-wolisho-brown transition-colors hover:border-wolisho-red hover:text-wolisho-red disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark done
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
